import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, map } from 'rxjs';
import { SelectOption } from '@app/interfaces';
import { StandardApiResponse } from '@app/interfaces/standard-api-response';
import { IEditService, IListService } from '@app/services';
import { ApiResponse, SiteEntity, SiteListItem, SiteListItemBase } from '@app/shared/models';
import { mapArrayToSelectOptions } from '@app/utils';

@Injectable({ providedIn: 'root' })
export class SiteService implements IListService, IEditService<SiteEntity> {
	endpointBase = '/api/site-cf';

	constructor(private readonly http: HttpClient) {}

	// IListService implementation
	getList() {
		return this.http.get<ApiResponse<'sites', SiteListItem[]>>(`${this.endpointBase}/list`);
	}

	getSite(id: number) {
		return this.http.get<StandardApiResponse<SiteEntity>>(`${this.endpointBase}/${id}`);
	}

	getSitesByCustomer(customerId: number) {
		return this.http.get<SiteListItemBase[]>(`${this.endpointBase}/get-by-customer/${customerId}`);
	}

	// IEditService implementation
	getById(id: number) {
		return this.getSite(id).pipe(map((response) => response.data));
	}

	// IEditService implementation
	createEntity(entity: SiteEntity) {
		return this.http.put<StandardApiResponse<number>>(`${this.endpointBase}/entity`, entity);
	}

	// IEditService implementation
	updateEntity(entity: SiteEntity) {
		return this.http.post<SiteEntity>(`${this.endpointBase}/entity`, entity);
	}

	// IEditService and IListService implementation
	deleteEntity(id: number): Observable<any> {
		return this.http.delete(`${this.endpointBase}/${id}`);
	}

	// IListService implementation
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	bulkDeleteEntities?(ids: number[] | string[]): Observable<any> {
		return EMPTY;
	}

	validateSiteCode(siteCode: string, siteId: number) {
		return this.http.get<boolean>(`${this.endpointBase}/validate-site-code`, {
			params: {
				siteCode,
				siteId: siteId?.toString(),
			},
		});
	}

	sitesSelectOptions(sites: SiteListItemBase[]): SelectOption[] {
		const options = mapArrayToSelectOptions(sites, {
			value: 'Id',
			label: (site: SiteListItemBase) => `${site.SiteName} - ${site.SiteCode}`,
			all: true,
			allLabel: 'All',
			allValue: 0,
		});
		return options;
	}

	timeZoneSelectOptions(timeZoneList: { Id: number; Description: string }[], defaultTimeZoneId: number): SelectOption[] {
		const defaultTimeZone = timeZoneList.find((tz: any) => tz.Id === defaultTimeZoneId);
		const defaultDescription = defaultTimeZone ? `${defaultTimeZone.Description} (Default)` : undefined;
		const output = mapArrayToSelectOptions(
			timeZoneList.filter((tz: any) => tz.Id !== defaultTimeZoneId),
			{
				value: 'Id',
				label: 'Description',
				all: !!defaultDescription,
				allValue: null, // TODO: you can't select a row with a null value
				allLabel: defaultDescription,
				sort: 'value',
			}
		);

		return output;
	}
}
